import type { AsyncStorage, PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { createStore, get as IDBGet, del as IDBRemove, set as IDBSet } from 'idb-keyval';
import { noop } from 'lodash-es';
import SuperJSON from 'superjson';
import { EsThread, EsThreadPool } from 'threads-es';
import type { PersistStorage } from 'zustand/middleware/persist';
import type { StorageWorkerAPI } from '@/assets/workers/storage.worker';
import StorageWorker from '@/assets/workers/storage.worker?worker';
import { SENTRY_LOG, simpleLogger } from '@/services/sentry';
import { getActiveUserId } from '@/stores/auth';
import { requestRestart } from '@/stores/device';
import { QUERIES_STORAGE_KEY, STORAGE_KEY_PREFIX } from '@/utils/constants';
import { readableFileSize } from '@/utils/file-size';
import { createLogger } from '@/utils/logger';

export type StorageDriver = 'INDEXEDDB' | 'LOCAL_STORAGE' | 'SESSION_STORAGE';

export default class StorageManager {
  readonly logger = createLogger('Storage');
  #workerPool: EsThreadPool<StorageWorkerAPI> | null = null;

  constructor() {
    StorageManager.#createWorkerPool()
      .then((pool) => {
        this.#workerPool = pool;
      })
      .catch((error) => {
        this.logger.error(error);
      });
  }

  async get<K>(driver: StorageDriver, key: string, userId?: number): Promise<K | null> {
    const activeUserId = userId ?? getActiveUserId();

    const storeKey = activeUserId ? `${activeUserId}/${key}` : key;
    switch (driver) {
      case 'LOCAL_STORAGE': {
        const value = localStorage.getItem(storeKey);
        if (!value) return null;
        return SuperJSON.parse(value);
      }
      case 'SESSION_STORAGE': {
        const value = sessionStorage.getItem(storeKey);
        if (!value) return null;
        return SuperJSON.parse(value);
      }
      default:
        return (await this.#dispatch('get', storeKey)) as Promise<K | null>;
    }
  }

  async set(driver: StorageDriver, key: string, value: unknown, userId?: number) {
    const activeUserId = userId ?? getActiveUserId();

    const storeKey = activeUserId ? `${activeUserId}/${key}` : key;
    switch (driver) {
      case 'LOCAL_STORAGE':
        localStorage.setItem(storeKey, SuperJSON.stringify(value));
        break;
      case 'SESSION_STORAGE':
        sessionStorage.setItem(storeKey, SuperJSON.stringify(value));
        break;
      default:
        await this.#queue('set', storeKey, value);
        break;
    }
  }

  async remove(driver: StorageDriver, key: string, userId?: number) {
    const activeUserId = userId ?? getActiveUserId();

    const storeKey = activeUserId ? `${activeUserId}/${key}` : key;
    switch (driver) {
      case 'LOCAL_STORAGE':
        localStorage.removeItem(storeKey);
        break;
      case 'SESSION_STORAGE':
        sessionStorage.removeItem(storeKey);
        break;
      default:
        await this.#queue('remove', storeKey);
        break;
    }
  }

  async clear(userId?: number) {
    const activeUserId = userId ?? getActiveUserId();
    if (activeUserId === undefined) throw 'INVALID_USER_ID';

    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(`${activeUserId}/${STORAGE_KEY_PREFIX}/`)) {
        localStorage.removeItem(key);
      }
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(`${activeUserId}/${STORAGE_KEY_PREFIX}/`)) {
        sessionStorage.removeItem(key);
      }
    }
    await this.#queue('clear', activeUserId);
  }

  async clearAll() {
    for (const key of Object.keys(localStorage)) {
      if (key.includes(`/${STORAGE_KEY_PREFIX}/`)) {
        localStorage.removeItem(key);
      }
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.includes(`/${STORAGE_KEY_PREFIX}/`)) {
        sessionStorage.removeItem(key);
      }
    }
    await this.#queue('clearAll');
  }

  async #dispatch<K extends keyof StorageWorkerAPI>(action: K, ...params: Parameters<StorageWorkerAPI[K]>) {
    return StorageManager.#createWorker().then(async (worker) => {
      if (typeof worker.methods[action] !== 'function') throw `Invalid worker action: ${String(action)}`;

      try {
        return await worker.methods[action](...params);
      } catch (error) {
        this.logger.error(action, params, error);
        throw error;
      } finally {
        await worker.terminate();
      }
    });
  }

  async #queue<K extends keyof StorageWorkerAPI>(action: K, ...params: Parameters<StorageWorkerAPI[K]>) {
    if (!this.#workerPool) {
      this.#workerPool = await StorageManager.#createWorkerPool();
    }

    try {
      return this.#workerPool.queue(async (worker) => {
        if (typeof worker.methods[action] !== 'function') throw `Invalid worker action: ${String(action)}`;

        return await worker.methods[action](...params);
      });
    } catch (error) {
      this.logger.error(action, params, error);

      throw error;
    }
  }

  static #createWorker() {
    return EsThread.Spawn<StorageWorkerAPI>(new StorageWorker({ name: 'storage-worker' }), { timeout: 15_000 });
  }

  static #createWorkerPool() {
    return EsThreadPool.Spawn(
      (id) =>
        EsThread.Spawn<StorageWorkerAPI>(new StorageWorker({ name: `storage-worker-${id}` }), { timeout: 15_000 }),
      { size: 3 },
    );
  }
}

export const storageManager = new StorageManager();

export async function reportStorageError(
  error: unknown,
  key: string,
  operation: string,
  driver = 'unknown',
): Promise<void> {
  const attributes = {
    key,
    operation,
    driver,
  };

  if (error instanceof Error && error.message === 'UnknownError') {
    storageManager.logger.fatal(error, attributes);
    requestRestart();
  } else {
    const estimate = await estimateStorageQuota();
    if (estimate) Object.assign(attributes, estimate);
    storageManager.logger.warn(error, attributes);
  }

  return simpleLogger({
    name: SENTRY_LOG.STORAGE,
    requestAttributes: attributes,
  });
}

export function createStorageByUserId<T = unknown>(
  type: StorageDriver = 'LOCAL_STORAGE',
  userId?: number,
): PersistStorage<T> {
  if (process.env.NODE_ENV === 'test') {
    return {
      getItem: <S>(): Promise<S | null> => Promise.resolve(null),
      setItem: () => Promise.resolve(),
      removeItem: () => Promise.resolve(),
    };
  }

  return {
    getItem: <S>(name: string): Promise<S | null> => storageManager.get(type, name, userId),
    setItem: (name, value) => storageManager.set(type, name, value, userId),
    removeItem: (name) => storageManager.remove(type, name, userId),
  };
}

export function createQueryPersister(name: IDBValidKey = QUERIES_STORAGE_KEY) {
  return {
    persistClient: (client) => storageManager.set('INDEXEDDB', name.toString(), client),
    restoreClient: async () => {
      const value = await storageManager.get<PersistedClient>('INDEXEDDB', name.toString());
      return value ?? undefined;
    },
    removeClient: () => storageManager.remove('INDEXEDDB', name.toString()),
  } satisfies Persister;
}

export function createNotificationStorage<S>(): AsyncStorage<S> {
  const store = createStore('notification-store', 'notification-store');

  return {
    getItem: (name) => IDBGet(name, store),
    setItem: (name, value) => IDBSet(name, value, store),
    removeItem: (name) => IDBRemove(name, store),
  };
}

export function removeStoragesByUserId(userId: number) {
  storageManager.clear(userId).catch(noop);
  indexedDB.deleteDatabase(`message-store-${userId}`);
  indexedDB.deleteDatabase('notification-store');
}

export async function removeStorages() {
  for (const database of await indexedDB.databases()) {
    const name = database.name;
    if (!name) continue;
    if (name.startsWith('message-store-') || name === 'notification-store' || name === 'log-store') {
      indexedDB.deleteDatabase(name);
    }
  }

  await storageManager.clearAll();
}

export async function estimateStorageQuota() {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage && typeof navigator.storage.estimate === 'function') {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();

      return {
        usage: readableFileSize(usage),
        quota: readableFileSize(quota),
        ratio: Number.parseFloat(((usage / quota) * 100).toFixed(2)),
      };
    }

    return {
      usage: '0B',
      quota: '0B',
      ratio: 0.0,
    };
  } catch {
    return {
      usage: '0B',
      quota: '0B',
      ratio: 0.0,
    };
  }
}
